import type { Extension } from "@medplum/fhirtypes";
import {
  ACTION_PARAMETER_VALUE_URL,
  type ActionParameterValue,
  buildActionParameterValueExtensions,
  parseActionParameterValues,
  TIME_WINDOW_PRESETS,
} from "@repo/api";
import type { ProtocolDefinitionDto } from "@repo/data-store";
import { Badge } from "@repo/ui/components/ui/badge";
import { Label } from "@repo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { useMemo } from "react";
import type { ProtocolFormValues } from "@/features/catalog/forms/use-plan-definition-form";
import { ParameterConfigurator } from "@/shared/common/parameter-configurator";
import { ProcedurePicker } from "./procedure-picker";
import type { ProtocolBuilderProcedure } from "./protocol-builder-definition";

const TIME_WINDOW_ITEMS = Object.fromEntries(
  Object.entries(TIME_WINDOW_PRESETS).map(([value, preset]) => [value, preset.label]),
);

const TIME_WINDOW_OPTIONS = Object.entries(TIME_WINDOW_PRESETS).map(([value, preset]) => ({
  value,
  label: preset.label,
}));

export interface ProtocolEditorProps {
  protocol: ProtocolFormValues;
  onProtocolChange: (updated: ProtocolFormValues) => void;
  protocolPlanDefinitions?: ProtocolDefinitionDto[];
  activityDefinitions?: ProtocolBuilderProcedure[];
}

function extractActivityDefinitionId(canonical: string | undefined): string | undefined {
  if (!canonical?.startsWith("ActivityDefinition/")) return undefined;
  return canonical.replace("ActivityDefinition/", "");
}

export function ProtocolEditor({
  protocol,
  onProtocolChange,
  protocolPlanDefinitions = [],
  activityDefinitions = [],
}: ProtocolEditorProps) {
  const canonical = protocol.definitionCanonical;

  const adId = extractActivityDefinitionId(canonical);
  const activityDefinition = adId ? activityDefinitions.find((ad) => ad.resourceId === adId) : undefined;
  const adParameters = activityDefinition?.legacyParameters ?? [];

  const parameterValues = useMemo(() => {
    return parseActionParameterValues(protocol.extension as Extension[] | undefined);
  }, [protocol.extension]);

  const parameterBadges = useMemo(() => {
    if (parameterValues.length === 0 || adParameters.length === 0) return [];
    const paramMap = new Map(adParameters.map((p) => [p.name, p.text]));
    return parameterValues.map((v) => ({
      label: paramMap.get(v.name) ?? v.name,
      display: v.display,
    }));
  }, [parameterValues, adParameters]);

  const handleDefinitionChange = (newCanonical: string | undefined) => {
    const pd = protocolPlanDefinitions.find((candidate) => candidate.definitionUrl === newCanonical);
    const otherExtensions = ((protocol.extension as Extension[]) ?? []).filter(
      (e) => e.url !== ACTION_PARAMETER_VALUE_URL,
    );
    onProtocolChange({
      ...protocol,
      definitionCanonical: newCanonical,
      title: pd?.name || "",
      description: pd?.description || "",
      extension: otherExtensions.length > 0 ? otherExtensions : undefined,
    });
  };

  const handleParameterChange = (values: ActionParameterValue[]) => {
    const otherExtensions = ((protocol.extension as Extension[]) ?? []).filter(
      (e) => e.url !== ACTION_PARAMETER_VALUE_URL,
    );
    const paramExtensions = buildActionParameterValueExtensions(values);
    const allExtensions = [...otherExtensions, ...paramExtensions];
    onProtocolChange({
      ...protocol,
      extension: allExtensions.length > 0 ? allExtensions : undefined,
    });
  };

  return (
    <div className="space-y-4 p-4">
      <ProcedurePicker
        mode="program"
        value={canonical}
        planDefinitions={protocolPlanDefinitions}
        onChange={handleDefinitionChange}
      />

      <div className="space-y-2">
        <Label>Time of Day</Label>
        <Select
          value={protocol.timeWindow || null}
          onValueChange={(v) => {
            if (v) onProtocolChange({ ...protocol, timeWindow: v as ProtocolFormValues["timeWindow"] });
          }}
          items={TIME_WINDOW_ITEMS}
        >
          <SelectTrigger className="w-full mt-1">
            {protocol.timeWindow ? (
              <SelectValue />
            ) : (
              <SelectValue>
                <span className="text-muted-foreground">Select time window...</span>
              </SelectValue>
            )}
          </SelectTrigger>
          <SelectContent>
            {TIME_WINDOW_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {parameterBadges.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {parameterBadges.map((b) => (
            <Badge key={b.label} variant="secondary" className="text-xs font-normal">
              <span className="text-muted-foreground">{b.label}:</span> {b.display}
            </Badge>
          ))}
        </div>
      )}

      {adParameters.length > 0 && (
        <ParameterConfigurator parameters={adParameters} values={parameterValues} onChange={handleParameterChange} />
      )}
    </div>
  );
}
